<template>
    <student-backend-focus-layout
        content-bg-color="gray-125"
        exit-button-text="Exit Deck"
        v-on:exit-page-clicked="goToFlashcardsIndex"
        :page-title="stackTitle"
    >
        <template #content>
            <div
                class="flex h-full w-full flex-col items-center justify-center"
            >
                <div
                    class="mb-4 text-[17px] font-semibold text-gray-700 md:hidden"
                >
                    {{ stackTitle }}
                </div>

                <div
                    class="relative mx-auto mb-12 aspect-[3/2] w-full max-w-3xl flex-grow bg-white shadow hover:cursor-pointer md:flex-none"
                >
                    <div
                        v-if="stackFinished"
                        class="flex h-full flex-col items-center justify-center gap-4"
                    >
                        <div class="">
                            <new-progress-ring
                                :percent-filled="Math.round(score * 100)"
                                height="144px"
                            >
                                <div
                                    class="flex flex-col items-center justify-center"
                                >
                                    <div class="text-[16px] text-gray-600">
                                        Score
                                    </div>
                                    <div class="text-[20px]">
                                        {{ Math.round(score * 100) }}%
                                    </div>
                                </div>
                            </new-progress-ring>
                        </div>
                        <div
                            class="text-center text-[26px] font-semibold text-gray-925"
                        >
                            <span v-if="score >= 0.8">Great Job!</span>
                            <span v-else-if="score >= 0.5">Good Start</span>
                        </div>
                        <div class="px-8 text-center text-[21px] text-gray-925">
                            You scored {{ Math.round(score * 100) }}% ({{
                                flashcardStack.flashcards.length - missedCount
                            }}
                            / {{ flashcardStack.flashcards.length }}) on this
                            deck. You can either return to your dashboard or try
                            again.
                        </div>
                        <div class="flex items-center justify-center gap-2">
                            <div
                                class="rounded border border-gray-400 px-2 py-2 text-[15px] hover:bg-gray-200/20"
                                @click="restartDeck"
                            >
                                Try Again
                            </div>
                            <div
                                class="rounded border bg-primary-525 px-2 py-2 text-[15px] text-white hover:bg-primary-600"
                                @click="goToFlashcardsIndex"
                            >
                                Back to Dashboard
                            </div>
                        </div>
                    </div>

                    <div
                        v-else
                        class="relative flex flex-col justify-between"
                        @click="showingCardFront ? goForward() : goBack()"
                    >
                        <div
                            class="flex items-center justify-center gap-2 p-4 pb-0 text-[15px] text-gray-600"
                        >
                            <div class="w-32 flex-none">
                                <div
                                    class="rounded border border-red-400 px-2 py-1 text-center text-[16px] text-red-400"
                                    v-if="
                                        cardPreviouslyMissed(
                                            currentFlashcard.id
                                        )
                                    "
                                >
                                    Past Mistake
                                </div>
                            </div>
                            <div
                                class="flex flex-grow items-center justify-center gap-2"
                            >
                                <div class="">
                                    {{ currentFlashcardIndex + 1 }}
                                </div>
                                <div class="">of</div>
                                <div class="">
                                    {{ flashcardStack.flashcards.length }}
                                </div>
                            </div>
                            <div class="w-32 flex-none">&nbsp;</div>
                        </div>
                        <!-- card body-->
                        <div
                            class="mb-auto flex h-[calc(100vh-410px)] flex-col justify-center overflow-y-auto text-[21px] leading-loose md:h-[410px]"
                            v-if="sending"
                        >
                            <div class="flex justify-center">
                                <svg
                                    aria-hidden="true"
                                    class="mr-2 h-8 w-8 animate-spin fill-primary-600 text-gray-100"
                                    viewBox="0 0 100 101"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                        fill="currentColor"
                                    />
                                    <path
                                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                        fill="currentFill"
                                    />
                                </svg>
                            </div>
                        </div>
                        <div
                            class="mb-auto flex h-[calc(100vh-410px)] flex-col justify-center overflow-y-auto py-4 text-[21px] leading-9 text-gray-725 md:h-[410px]"
                            v-else
                        >
                            <div
                                class="px-4 py-8 text-center md:px-12 lg:px-16"
                                v-if="showingCardFront"
                            >
                                <div
                                    class="whitespace-pre-wrap"
                                    v-html="currentFlashcard.front_content"
                                ></div>
                            </div>
                            <div
                                class="px-4 py-8 text-center md:px-12 lg:px-16"
                                v-if="showingCardBack"
                            >
                                <div
                                    class="whitespace-pre-wrap"
                                    v-html="currentFlashcard.back_content"
                                ></div>
                            </div>
                        </div>
                        <!-- controls tutorial-->
                        <div
                            v-if="currentFlashcardIndex === 0"
                            class="hidden border-t border-gray-300 md:block"
                        >
                            <div
                                class="flex items-center justify-center gap-1 py-4 text-[15px] text-gray-600"
                            >
                                <div class="">
                                    Click on card or use the arrows on your
                                    keyboard
                                </div>
                                <div class="ml-2 text-gray-500">
                                    <svg
                                        width="29"
                                        height="30"
                                        viewBox="0 0 29 30"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <rect
                                            x="0.5"
                                            y="29"
                                            width="28"
                                            height="28"
                                            rx="3.5"
                                            transform="rotate(-90 0.5 29)"
                                            fill="white"
                                            stroke="currentColor"
                                        />
                                        <path
                                            d="M20.8337 14.5L9.16699 14.5"
                                            stroke="currentColor"
                                            stroke-width="1.66667"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                        <path
                                            d="M15.0003 20.333L9.16699 14.4997L15.0003 8.66634"
                                            stroke="currentColor"
                                            stroke-width="1.66667"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </div>
                                <div class="mr-2 text-gray-500">
                                    <svg
                                        width="29"
                                        height="30"
                                        viewBox="0 0 29 30"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <rect
                                            x="-0.5"
                                            y="-0.5"
                                            width="28"
                                            height="28"
                                            rx="3.5"
                                            transform="matrix(-4.37114e-08 -1 -1 4.37114e-08 28 28.5)"
                                            fill="white"
                                            stroke="currentColor"
                                        />
                                        <path
                                            d="M8.16634 14.5L19.833 14.5"
                                            stroke="currentColor"
                                            stroke-width="1.66667"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                        <path
                                            d="M13.9997 20.333L19.833 14.4997L13.9997 8.66634"
                                            stroke="currentColor"
                                            stroke-width="1.66667"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </div>
                                <div class="">to flip</div>
                            </div>
                        </div>
                        <div
                            v-if="currentFlashcardIndex === 0"
                            class="border-t border-gray-300 py-4 text-center text-[15px] text-gray-600 md:hidden"
                        >
                            Click on the card to flip
                        </div>
                    </div>
                </div>
                <div class="mb-8 h-24 flex-none">
                    <div
                        class="flex items-start justify-center gap-4"
                        v-if="!stackFinished && showingCardBack && !sending"
                    >
                        <div class="flex items-start justify-center gap-2">
                            <div
                                class="text-red-400 hover:cursor-pointer hover:text-red-500"
                                @click="sendCardStatus(true)"
                            >
                                <svg
                                    width="127"
                                    height="110"
                                    viewBox="0 0 127 110"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        cx="63.5"
                                        cy="33"
                                        r="33"
                                        fill="currentColor"
                                    />
                                    <g clip-path="url(#clip0_1316_18565)">
                                        <path
                                            d="M60.5292 22.2944C59.985 22.1486 59.3982 22.3153 59.0639 22.7692C58.3245 23.7703 57.7802 24.9016 57.4593 26.104C56.8589 28.345 57.0742 30.6096 57.9208 32.5878C58.0482 32.8854 58.2924 33.1114 58.5529 33.302C59.0958 33.6987 59.5162 34.2656 59.7879 34.9011C60.4421 36.4357 61.4094 37.8168 62.628 38.9561C63.4221 39.6959 64.0287 40.6435 64.1563 41.7194C64.2464 42.4734 64.1916 43.2376 63.9949 43.971L63.7778 44.7816C63.7095 45.0363 63.7452 45.3077 63.8771 45.5361C64.0089 45.7645 64.2261 45.9311 64.4808 45.9993C65.245 46.2041 66.0592 46.0969 66.7443 45.7014C67.4294 45.3058 67.9293 44.6543 68.1341 43.8901C68.5294 42.4149 68.5708 40.9286 68.3124 39.5212C68.1633 38.7154 68.8893 37.9163 69.6807 38.1283L73.6837 39.2009C74.9976 39.553 76.4125 38.9796 76.9024 37.7096C77.1048 37.1846 77.2812 36.6444 77.4304 36.0874C78.3746 32.5848 78.0881 28.8642 76.6189 25.5474C76.2874 24.7971 75.6051 24.2752 74.8137 24.0632L69.671 22.6852C69.0525 22.5195 68.4098 22.4543 67.7699 22.4915L63.4254 22.7548C62.7854 22.7927 62.1434 22.7273 61.5242 22.561L60.5292 22.2944ZM51.2519 30.4769C51.0269 28.4942 51.1778 26.4868 51.6969 24.5599C52.103 23.0378 52.7329 21.5845 53.5661 20.2475C54.1907 19.2482 55.4236 18.8676 56.562 19.1727L57.7235 19.4839C58.2933 19.6366 58.4746 20.3686 58.0851 20.8133C56.8797 22.188 56.0078 23.8223 55.5372 25.589C54.9512 27.7762 55.0128 29.9845 55.6035 32.0108C55.779 32.6109 55.2386 33.2292 54.6342 33.0673L53.2883 32.7066C52.2229 32.4212 51.3782 31.5731 51.2519 30.4769Z"
                                            fill="white"
                                        />
                                    </g>
                                    <path
                                        d="M11.6015 83.6818V97.5H9.92818V83.6818H11.6015ZM16.3329 91.2656V97.5H14.7406V87.1364H16.2789V88.7557H16.4139C16.6568 88.2294 17.0256 87.8066 17.5204 87.4872C18.0152 87.1634 18.6539 87.0014 19.4366 87.0014C20.1383 87.0014 20.7523 87.1454 21.2786 87.4332C21.8049 87.7166 22.2142 88.1484 22.5066 88.7287C22.7989 89.3045 22.9451 90.0331 22.9451 90.9148V97.5H21.3528V91.0227C21.3528 90.2086 21.1414 89.5743 20.7186 89.12C20.2958 88.6612 19.7155 88.4318 18.9778 88.4318C18.4695 88.4318 18.0152 88.542 17.6149 88.7624C17.219 88.9828 16.9064 89.3045 16.677 89.7273C16.4476 90.1501 16.3329 90.6629 16.3329 91.2656ZM30.0651 97.7159C29.0935 97.7159 28.2568 97.4865 27.5551 97.0277C26.8534 96.5689 26.3137 95.9369 25.9358 95.1317C25.558 94.3266 25.3691 93.4067 25.3691 92.3722C25.3691 91.3196 25.5625 90.3907 25.9493 89.5856C26.3406 88.7759 26.8849 88.1439 27.5821 87.6896C28.2838 87.2308 29.1025 87.0014 30.0381 87.0014C30.7668 87.0014 31.4235 87.1364 32.0083 87.4062C32.593 87.6761 33.0721 88.054 33.4454 88.5398C33.8187 89.0256 34.0504 89.5923 34.1404 90.2401H32.548C32.4266 89.7678 32.1567 89.3494 31.7384 88.9851C31.3245 88.6162 30.7668 88.4318 30.0651 88.4318C29.4443 88.4318 28.9001 88.5937 28.4323 88.9176C27.969 89.237 27.6069 89.689 27.346 90.2738C27.0896 90.854 26.9614 91.5355 26.9614 92.3182C26.9614 93.1188 27.0873 93.8161 27.3392 94.4098C27.5956 95.0036 27.9555 95.4646 28.4188 95.793C28.8866 96.1213 29.4353 96.2855 30.0651 96.2855C30.4789 96.2855 30.8545 96.2135 31.1919 96.0696C31.5292 95.9257 31.8148 95.7188 32.0487 95.4489C32.2826 95.179 32.4491 94.8551 32.548 94.4773H34.1404C34.0504 95.089 33.8277 95.64 33.4724 96.1303C33.1215 96.6161 32.656 97.003 32.0757 97.2908C31.5 97.5742 30.8298 97.7159 30.0651 97.7159ZM40.6784 97.7159C39.7427 97.7159 38.9218 97.4933 38.2156 97.0479C37.5139 96.6026 36.9652 95.9796 36.5693 95.179C36.178 94.3783 35.9823 93.4427 35.9823 92.3722C35.9823 91.2926 36.178 90.3503 36.5693 89.5451C36.9652 88.7399 37.5139 88.1147 38.2156 87.6694C38.9218 87.2241 39.7427 87.0014 40.6784 87.0014C41.614 87.0014 42.4326 87.2241 43.1343 87.6694C43.8405 88.1147 44.3893 88.7399 44.7806 89.5451C45.1765 90.3503 45.3744 91.2926 45.3744 92.3722C45.3744 93.4427 45.1765 94.3783 44.7806 95.179C44.3893 95.9796 43.8405 96.6026 43.1343 97.0479C42.4326 97.4933 41.614 97.7159 40.6784 97.7159ZM40.6784 96.2855C41.3891 96.2855 41.9738 96.1033 42.4326 95.739C42.8914 95.3746 43.231 94.8956 43.4514 94.3018C43.6718 93.7081 43.782 93.0649 43.782 92.3722C43.782 91.6795 43.6718 91.034 43.4514 90.4357C43.231 89.8375 42.8914 89.3539 42.4326 88.9851C41.9738 88.6162 41.3891 88.4318 40.6784 88.4318C39.9677 88.4318 39.3829 88.6162 38.9241 88.9851C38.4653 89.3539 38.1257 89.8375 37.9053 90.4357C37.6849 91.034 37.5747 91.6795 37.5747 92.3722C37.5747 93.0649 37.6849 93.7081 37.9053 94.3018C38.1257 94.8956 38.4653 95.3746 38.9241 95.739C39.3829 96.1033 39.9677 96.2855 40.6784 96.2855ZM47.805 97.5V87.1364H49.3434V88.7017H49.4513C49.6403 88.1889 49.9821 87.7728 50.4769 87.4535C50.9717 87.1341 51.5295 86.9744 52.1502 86.9744C52.2672 86.9744 52.4134 86.9767 52.5888 86.9812C52.7642 86.9857 52.8969 86.9924 52.9869 87.0014V88.6207C52.9329 88.6072 52.8092 88.587 52.6158 88.56C52.4268 88.5285 52.2267 88.5128 52.0153 88.5128C51.5115 88.5128 51.0617 88.6185 50.6658 88.8299C50.2745 89.0368 49.9641 89.3247 49.7347 89.6935C49.5098 90.0579 49.3974 90.474 49.3974 90.9418V97.5H47.805ZM54.8744 97.5V87.1364H56.4127V88.7017H56.5207C56.7096 88.1889 57.0515 87.7728 57.5463 87.4535C58.041 87.1341 58.5988 86.9744 59.2195 86.9744C59.3365 86.9744 59.4827 86.9767 59.6581 86.9812C59.8335 86.9857 59.9662 86.9924 60.0562 87.0014V88.6207C60.0022 88.6072 59.8785 88.587 59.6851 88.56C59.4962 88.5285 59.296 88.5128 59.0846 88.5128C58.5808 88.5128 58.131 88.6185 57.7352 88.8299C57.3438 89.0368 57.0335 89.3247 56.8041 89.6935C56.5792 90.0579 56.4667 90.474 56.4667 90.9418V97.5H54.8744ZM65.9735 97.7159C64.9749 97.7159 64.1135 97.4955 63.3893 97.0547C62.6696 96.6094 62.1141 95.9886 61.7227 95.1925C61.3359 94.3918 61.1425 93.4607 61.1425 92.3991C61.1425 91.3376 61.3359 90.402 61.7227 89.5923C62.1141 88.7782 62.6584 88.1439 63.3556 87.6896C64.0573 87.2308 64.8759 87.0014 65.8115 87.0014C66.3513 87.0014 66.8843 87.0914 67.4106 87.2713C67.9369 87.4512 68.4159 87.7436 68.8477 88.1484C69.2796 88.5488 69.6237 89.0795 69.8801 89.7408C70.1365 90.402 70.2646 91.2161 70.2646 92.1832V92.858H62.276V91.4815H68.6453C68.6453 90.8968 68.5284 90.375 68.2945 89.9162C68.0651 89.4574 67.7367 89.0953 67.3094 88.8299C66.8866 88.5645 66.3873 88.4318 65.8115 88.4318C65.1773 88.4318 64.6285 88.5893 64.1652 88.9041C63.7064 89.2145 63.3533 89.6193 63.1059 90.1186C62.8585 90.6179 62.7348 91.1532 62.7348 91.7244V92.642C62.7348 93.4247 62.8698 94.0882 63.1396 94.6325C63.414 95.1722 63.7941 95.5838 64.2799 95.8672C64.7657 96.1461 65.3302 96.2855 65.9735 96.2855C66.3918 96.2855 66.7696 96.227 67.107 96.1101C67.4488 95.9886 67.7435 95.8087 67.9909 95.5703C68.2383 95.3274 68.4294 95.026 68.5644 94.6662L70.1027 95.098C69.9408 95.6198 69.6686 96.0786 69.2863 96.4744C68.904 96.8658 68.4317 97.1716 67.8694 97.392C67.3071 97.608 66.6752 97.7159 65.9735 97.7159ZM76.8971 97.7159C75.9255 97.7159 75.0889 97.4865 74.3872 97.0277C73.6855 96.5689 73.1457 95.9369 72.7678 95.1317C72.39 94.3266 72.2011 93.4067 72.2011 92.3722C72.2011 91.3196 72.3945 90.3907 72.7813 89.5856C73.1727 88.7759 73.7169 88.1439 74.4142 87.6896C75.1159 87.2308 75.9345 87.0014 76.8701 87.0014C77.5988 87.0014 78.2555 87.1364 78.8403 87.4062C79.425 87.6761 79.9041 88.054 80.2774 88.5398C80.6508 89.0256 80.8824 89.5923 80.9724 90.2401H79.3801C79.2586 89.7678 78.9887 89.3494 78.5704 88.9851C78.1566 88.6162 77.5988 88.4318 76.8971 88.4318C76.2764 88.4318 75.7321 88.5937 75.2643 88.9176C74.801 89.237 74.4389 89.689 74.178 90.2738C73.9216 90.854 73.7934 91.5355 73.7934 92.3182C73.7934 93.1188 73.9194 93.8161 74.1713 94.4098C74.4276 95.0036 74.7875 95.4646 75.2508 95.793C75.7186 96.1213 76.2674 96.2855 76.8971 96.2855C77.3109 96.2855 77.6865 96.2135 78.0239 96.0696C78.3612 95.9257 78.6469 95.7188 78.8808 95.4489C79.1147 95.179 79.2811 94.8551 79.3801 94.4773H80.9724C80.8824 95.089 80.6598 95.64 80.3044 96.1303C79.9536 96.6161 79.488 97.003 78.9078 97.2908C78.332 97.5742 77.6618 97.7159 76.8971 97.7159ZM87.8073 87.1364V88.4858H82.4365V87.1364H87.8073ZM84.0019 84.6534H85.5942V94.5312C85.5942 94.9811 85.6594 95.3184 85.7899 95.5433C85.9248 95.7637 86.0957 95.9122 86.3026 95.9886C86.5141 96.0606 86.7367 96.0966 86.9706 96.0966C87.146 96.0966 87.29 96.0876 87.4024 96.0696C87.5149 96.0471 87.6048 96.0291 87.6723 96.0156L87.9962 97.446C87.8882 97.4865 87.7375 97.527 87.5441 97.5675C87.3507 97.6125 87.1056 97.6349 86.8087 97.6349C86.3589 97.6349 85.9181 97.5382 85.4862 97.3448C85.0589 97.1514 84.7036 96.8568 84.4202 96.4609C84.1413 96.0651 84.0019 95.5658 84.0019 94.9631V84.6534Z"
                                        fill="#E25941"
                                    />
                                    <rect
                                        x="118.45"
                                        y="101.45"
                                        width="20.9"
                                        height="20.9"
                                        rx="2.48448"
                                        transform="rotate(180 118.45 101.45)"
                                        fill="#F1F3F8"
                                        stroke="#E25941"
                                        stroke-width="1.1"
                                    />
                                    <path
                                        d="M108 85.2588L108 96.7412"
                                        stroke="#E25941"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <path
                                        d="M113.741 91L108 96.7412L102.259 91"
                                        stroke="#E25941"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <defs>
                                        <clipPath id="clip0_1316_18565">
                                            <rect
                                                width="31.8174"
                                                height="31.8174"
                                                fill="white"
                                                transform="matrix(0.965926 0.258819 0.258819 -0.965926 44.5 43.7334)"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </div>
                            <div
                                class="text-green-500 hover:cursor-pointer hover:text-green-300"
                                @click="sendCardStatus(false)"
                            >
                                <svg
                                    width="114"
                                    height="110"
                                    viewBox="0 0 114 110"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        cx="57"
                                        cy="33"
                                        r="33"
                                        fill="currentColor"
                                    />
                                    <g clip-path="url(#clip0_1316_18578)">
                                        <path
                                            d="M54.0292 42.6734C53.485 42.8192 52.8982 42.6525 52.5639 42.1986C51.8245 41.1975 51.2802 40.0662 50.9593 38.8638C50.3589 36.6228 50.5742 34.3582 51.4208 32.38C51.5482 32.0823 51.7924 31.8563 52.0529 31.6658C52.5958 31.2691 53.0162 30.7022 53.2879 30.0667C53.9421 28.5321 54.9094 27.151 56.128 26.0117C56.9221 25.2719 57.5287 24.3242 57.6563 23.2483C57.7464 22.4944 57.6916 21.7302 57.4949 20.9968L57.2778 20.1862C57.2095 19.9315 57.2452 19.6601 57.3771 19.4317C57.5089 19.2033 57.7261 19.0367 57.9808 18.9684C58.745 18.7637 59.5592 18.8709 60.2443 19.2664C60.9294 19.662 61.4293 20.3135 61.6341 21.0776C62.0294 22.5528 62.0708 24.0391 61.8124 25.4465C61.6633 26.2524 62.3893 27.0515 63.1807 26.8394L67.1837 25.7668C68.4976 25.4148 69.9125 25.9882 70.4024 27.2582C70.6048 27.7832 70.7812 28.3233 70.9304 28.8804C71.8746 32.383 71.5881 36.1036 70.1189 39.4203C69.7874 40.1707 69.1051 40.6925 68.3137 40.9046L63.171 42.2826C62.5525 42.4483 61.9098 42.5135 61.2699 42.4763L56.9254 42.213C56.2854 42.175 55.6434 42.2405 55.0242 42.4067L54.0292 42.6734ZM44.7519 34.4908C44.5269 36.4736 44.6778 38.481 45.1969 40.4078C45.603 41.9299 46.2329 43.3833 47.0661 44.7203C47.6907 45.7196 48.9236 46.1001 50.062 45.7951L51.2235 45.4839C51.7933 45.3312 51.9746 44.5991 51.5851 44.1545C50.3797 42.7798 49.5078 41.1455 49.0372 39.3788C48.4512 37.1916 48.5128 34.9833 49.1035 32.957C49.279 32.3569 48.7386 31.7386 48.1342 31.9005L46.7883 32.2611C45.7229 32.5466 44.8782 33.3947 44.7519 34.4908Z"
                                            fill="white"
                                        />
                                    </g>
                                    <path
                                        d="M21.0138 88H19.3405C19.2416 87.5187 19.0684 87.0959 18.821 86.7315C18.5781 86.3672 18.2812 86.0613 17.9304 85.8139C17.584 85.562 17.1994 85.3731 16.7766 85.2472C16.3538 85.1212 15.913 85.0582 15.4541 85.0582C14.6175 85.0582 13.8596 85.2696 13.1804 85.6925C12.5056 86.1153 11.9681 86.7383 11.5678 87.5614C11.1719 88.3846 10.974 89.3944 10.974 90.5909C10.974 91.7874 11.1719 92.7972 11.5678 93.6204C11.9681 94.4435 12.5056 95.0665 13.1804 95.4893C13.8596 95.9122 14.6175 96.1236 15.4541 96.1236C15.913 96.1236 16.3538 96.0606 16.7766 95.9347C17.1994 95.8087 17.584 95.622 17.9304 95.3746C18.2812 95.1228 18.5781 94.8146 18.821 94.4503C19.0684 94.0814 19.2416 93.6586 19.3405 93.1818H21.0138C20.8879 93.888 20.6585 94.52 20.3256 95.0778C19.9927 95.6355 19.5789 96.1101 19.0841 96.5014C18.5893 96.8883 18.0338 97.1829 17.4176 97.3853C16.8058 97.5877 16.1514 97.6889 15.4541 97.6889C14.2756 97.6889 13.2276 97.401 12.31 96.8253C11.3924 96.2495 10.6704 95.4309 10.1441 94.3693C9.61785 93.3078 9.35471 92.0483 9.35471 90.5909C9.35471 89.1335 9.61785 87.8741 10.1441 86.8125C10.6704 85.7509 11.3924 84.9323 12.31 84.3565C13.2276 83.7808 14.2756 83.4929 15.4541 83.4929C16.1514 83.4929 16.8058 83.5941 17.4176 83.7965C18.0338 83.9989 18.5893 84.2958 19.0841 84.6871C19.5789 85.074 19.9927 85.5463 20.3256 86.104C20.6585 86.6573 20.8879 87.2893 21.0138 88ZM27.712 97.7159C26.7764 97.7159 25.9555 97.4933 25.2493 97.0479C24.5476 96.6026 23.9989 95.9796 23.603 95.179C23.2117 94.3783 23.016 93.4427 23.016 92.3722C23.016 91.2926 23.2117 90.3503 23.603 89.5451C23.9989 88.7399 24.5476 88.1147 25.2493 87.6694C25.9555 87.2241 26.7764 87.0014 27.712 87.0014C28.6477 87.0014 29.4663 87.2241 30.168 87.6694C30.8742 88.1147 31.423 88.7399 31.8143 89.5451C32.2102 90.3503 32.4081 91.2926 32.4081 92.3722C32.4081 93.4427 32.2102 94.3783 31.8143 95.179C31.423 95.9796 30.8742 96.6026 30.168 97.0479C29.4663 97.4933 28.6477 97.7159 27.712 97.7159ZM27.712 96.2855C28.4227 96.2855 29.0075 96.1033 29.4663 95.739C29.9251 95.3746 30.2647 94.8956 30.4851 94.3018C30.7055 93.7081 30.8157 93.0649 30.8157 92.3722C30.8157 91.6795 30.7055 91.034 30.4851 90.4357C30.2647 89.8375 29.9251 89.3539 29.4663 88.9851C29.0075 88.6162 28.4227 88.4318 27.712 88.4318C27.0013 88.4318 26.4166 88.6162 25.9578 88.9851C25.499 89.3539 25.1594 89.8375 24.939 90.4357C24.7186 91.034 24.6084 91.6795 24.6084 92.3722C24.6084 93.0649 24.7186 93.7081 24.939 94.3018C25.1594 94.8956 25.499 95.3746 25.9578 95.739C26.4166 96.1033 27.0013 96.2855 27.712 96.2855ZM34.8387 97.5V87.1364H36.3771V88.7017H36.485C36.674 88.1889 37.0158 87.7728 37.5106 87.4535C38.0054 87.1341 38.5632 86.9744 39.1839 86.9744C39.3009 86.9744 39.447 86.9767 39.6225 86.9812C39.7979 86.9857 39.9306 86.9924 40.0206 87.0014V88.6207C39.9666 88.6072 39.8429 88.587 39.6495 88.56C39.4605 88.5285 39.2604 88.5128 39.049 88.5128C38.5452 88.5128 38.0954 88.6185 37.6995 88.8299C37.3082 89.0368 36.9978 89.3247 36.7684 89.6935C36.5435 90.0579 36.4311 90.474 36.4311 90.9418V97.5H34.8387ZM41.9081 97.5V87.1364H43.4464V88.7017H43.5544C43.7433 88.1889 44.0852 87.7728 44.5799 87.4535C45.0747 87.1341 45.6325 86.9744 46.2532 86.9744C46.3702 86.9744 46.5164 86.9767 46.6918 86.9812C46.8672 86.9857 46.9999 86.9924 47.0899 87.0014V88.6207C47.0359 88.6072 46.9122 88.587 46.7188 88.56C46.5299 88.5285 46.3297 88.5128 46.1183 88.5128C45.6145 88.5128 45.1647 88.6185 44.7689 88.8299C44.3775 89.0368 44.0672 89.3247 43.8378 89.6935C43.6129 90.0579 43.5004 90.474 43.5004 90.9418V97.5H41.9081ZM53.0071 97.7159C52.0086 97.7159 51.1472 97.4955 50.423 97.0547C49.7033 96.6094 49.1478 95.9886 48.7564 95.1925C48.3696 94.3918 48.1762 93.4607 48.1762 92.3991C48.1762 91.3376 48.3696 90.402 48.7564 89.5923C49.1478 88.7782 49.692 88.1439 50.3892 87.6896C51.091 87.2308 51.9096 87.0014 52.8452 87.0014C53.385 87.0014 53.918 87.0914 54.4443 87.2713C54.9706 87.4512 55.4496 87.7436 55.8814 88.1484C56.3133 88.5488 56.6574 89.0795 56.9138 89.7408C57.1701 90.402 57.2983 91.2161 57.2983 92.1832V92.858H49.3097V91.4815H55.679C55.679 90.8968 55.5621 90.375 55.3282 89.9162C55.0988 89.4574 54.7704 89.0953 54.3431 88.8299C53.9203 88.5645 53.421 88.4318 52.8452 88.4318C52.211 88.4318 51.6622 88.5893 51.1989 88.9041C50.7401 89.2145 50.387 89.6193 50.1396 90.1186C49.8922 90.6179 49.7685 91.1532 49.7685 91.7244V92.642C49.7685 93.4247 49.9035 94.0882 50.1733 94.6325C50.4477 95.1722 50.8278 95.5838 51.3136 95.8672C51.7994 96.1461 52.3639 96.2855 53.0071 96.2855C53.4255 96.2855 53.8033 96.227 54.1407 96.1101C54.4825 95.9886 54.7772 95.8087 55.0245 95.5703C55.2719 95.3274 55.4631 95.026 55.5981 94.6662L57.1364 95.098C56.9745 95.6198 56.7023 96.0786 56.32 96.4744C55.9377 96.8658 55.4654 97.1716 54.9031 97.392C54.3408 97.608 53.7089 97.7159 53.0071 97.7159ZM63.9308 97.7159C62.9592 97.7159 62.1226 97.4865 61.4209 97.0277C60.7191 96.5689 60.1794 95.9369 59.8015 95.1317C59.4237 94.3266 59.2348 93.4067 59.2348 92.3722C59.2348 91.3196 59.4282 90.3907 59.815 89.5856C60.2064 88.7759 60.7506 88.1439 61.4478 87.6896C62.1495 87.2308 62.9682 87.0014 63.9038 87.0014C64.6325 87.0014 65.2892 87.1364 65.874 87.4062C66.4587 87.6761 66.9378 88.054 67.3111 88.5398C67.6845 89.0256 67.9161 89.5923 68.0061 90.2401H66.4138C66.2923 89.7678 66.0224 89.3494 65.6041 88.9851C65.1903 88.6162 64.6325 88.4318 63.9308 88.4318C63.3101 88.4318 62.7658 88.5937 62.298 88.9176C61.8347 89.237 61.4726 89.689 61.2117 90.2738C60.9553 90.854 60.8271 91.5355 60.8271 92.3182C60.8271 93.1188 60.9531 93.8161 61.2049 94.4098C61.4613 95.0036 61.8212 95.4646 62.2845 95.793C62.7523 96.1213 63.3011 96.2855 63.9308 96.2855C64.3446 96.2855 64.7202 96.2135 65.0576 96.0696C65.3949 95.9257 65.6806 95.7188 65.9145 95.4489C66.1484 95.179 66.3148 94.8551 66.4138 94.4773H68.0061C67.9161 95.089 67.6935 95.64 67.3381 96.1303C66.9873 96.6161 66.5217 97.003 65.9415 97.2908C65.3657 97.5742 64.6955 97.7159 63.9308 97.7159ZM74.841 87.1364V88.4858H69.4702V87.1364H74.841ZM71.0356 84.6534H72.6279V94.5312C72.6279 94.9811 72.6931 95.3184 72.8236 95.5433C72.9585 95.7637 73.1294 95.9122 73.3363 95.9886C73.5477 96.0606 73.7704 96.0966 74.0043 96.0966C74.1797 96.0966 74.3237 96.0876 74.4361 96.0696C74.5486 96.0471 74.6385 96.0291 74.706 96.0156L75.0299 97.446C74.9219 97.4865 74.7712 97.527 74.5778 97.5675C74.3844 97.6125 74.1392 97.6349 73.8424 97.6349C73.3926 97.6349 72.9517 97.5382 72.5199 97.3448C72.0926 97.1514 71.7373 96.8568 71.4539 96.4609C71.175 96.0651 71.0356 95.5658 71.0356 94.9631V84.6534Z"
                                        fill="#1F9294"
                                    />
                                    <rect
                                        x="-0.55"
                                        y="0.55"
                                        width="20.9"
                                        height="20.9"
                                        rx="2.48448"
                                        transform="matrix(-1 -8.74228e-08 -8.74228e-08 1 104.9 80)"
                                        fill="#F1F3F8"
                                        stroke="#1F9294"
                                        stroke-width="1.1"
                                    />
                                    <path
                                        d="M95 96.7412L95 85.2588"
                                        stroke="#1F9294"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <path
                                        d="M100.742 91L95.001 85.2588L89.2597 91"
                                        stroke="#1F9294"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                    <defs>
                                        <clipPath id="clip0_1316_18578">
                                            <rect
                                                width="31.8174"
                                                height="31.8174"
                                                fill="white"
                                                transform="translate(38 21.2344) rotate(-15)"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </student-backend-focus-layout>
</template>

<script>
import StudentBackendFocusLayout from "@/_Layouts/NewStudentBackendFocusLayout.vue";
import NewProgressRing from "@/Components/NewProgressRing.vue";

export default {
    name: "StudentFlashcardShowStack",
    components: {
        NewProgressRing,
        StudentBackendFocusLayout,
    },
    props: {
        flashcardStack: {
            type: Object,
            default: {},
        },
        flashcardHistory: {
            type: Object,
            default: {},
        },
    },
    data() {
        return {
            flashcardStackTimer: null,
            timeSpent: 0,
            currentFlashcardIndex: 0,
            showingCardFront: true,
            showingCardBack: false,
            sending: false,
            stackFinished: false,
            missedCount: 0,
        };
    },
    methods: {
        restartDeck() {
            this.$inertia.get(
                route("student.flashcards.show-stack", {
                    flashcard_stack: this.flashcardStack.id,
                })
            );
        },
        goToFlashcardsIndex() {
            this.$inertia.get(route("student.flashcards.index"));
        },
        goForward() {
            if (this.showingCardFront) {
                this.showingCardFront = false;
                this.showingCardBack = true;
            } else {
                this.currentFlashcardIndex++;
                if (
                    this.currentFlashcardIndex ===
                    this.flashcardStack.flashcards.length
                ) {
                    this.finishStack();
                } else {
                    this.showingCardFront = true;
                    this.showingCardBack = false;
                }
            }
        },
        goBack() {
            this.showingCardFront = true;
            this.showingCardBack = false;
        },
        sendCardStatus(wasMissed) {
            let vm = this;
            vm.sending = true;
            axios
                .post(route("student.flashcards.mark-card"), {
                    stackId: this.flashcardStack.id,
                    cardId: this.currentFlashcard.id,
                    missed: wasMissed,
                })
                .then(function () {
                    vm.goForward();
                    vm.sending = false;
                    if (wasMissed) {
                        vm.missedCount++;
                    }
                });
        },
        finishStack() {
            let vm = this;

            vm.stackFinished = true;
            axios.post(
                route("student.flashcards.finish-stack", {
                    flashcard_stack: this.flashcardStack.id,
                }),
                {
                    time_spent: vm.timeSpent,
                }
            );
        },
        cardPreviouslyMissed(cardId) {
            return _.includes(this.flashcardHistory.missed, cardId);
        },
    },
    computed: {
        stackTitle() {
            return this.flashcardStack.title;
        },
        currentFlashcard() {
            return this.flashcardStack.flashcards[this.currentFlashcardIndex];
        },
        score() {
            return (
                (this.flashcardStack.flashcards.length - this.missedCount) /
                this.flashcardStack.flashcards.length
            );
        },
    },
    created() {
        let vm = this;

        window.addEventListener("keydown", (event) => {
            if (event.key === "ArrowRight" && !this.showingCardBack) {
                vm.goForward();
            }
            if (event.key === "ArrowLeft" && !this.showingCardFront) {
                vm.goBack();
            }
            if (event.key === "ArrowUp" && this.showingCardBack) {
                vm.sendCardStatus(false);
            }
            if (event.key === "ArrowDown" && this.showingCardBack) {
                vm.sendCardStatus(true);
            }
        });

        this.flashcardStackTimer = setInterval(function () {
            vm.timeSpent++;
        }, 1000);
    },
    unmounted() {
        clearInterval(this.flashcardStackTimer);
    },
};
</script>
